'use client';

import { useState, useEffect, useMemo } from 'react';
import { IconType } from 'react-icons';
import {
  FiChevronLeft, FiChevronRight, FiExternalLink, FiTag,
} from 'react-icons/fi';
import {
  FaFire, FaPlane, FaMobileAlt, FaTshirt, FaUtensils, FaDesktop,
  FaShoppingCart, FaHome, FaHeart, FaCar, FaGamepad, FaBaby,
  FaBookOpen, FaDumbbell, FaPills, FaGift, FaBriefcase, FaMusic,
  FaCamera, FaWrench, FaBus, FaBicycle, FaGlasses, FaShoePrints,
  FaGem, FaFlask, FaPizzaSlice, FaTv, FaHotel, FaBlender,
  FaRunning, FaDrumstickBite, FaCapsules,
} from 'react-icons/fa';
import { useDynamicTheme } from '@/components/DynamicThemeProvider';
import { useTheme } from '@/components/ThemeProvider';
import { getCoupons, getCategories } from '@/services/api';
import { getServerUrl, getImageUrl } from '@/utils/serverUrl';

const ICON_MAP: Record<string, IconType> = {
  'most-used': FaFire, popular: FaFire, trending: FaFire, hot: FaFire,
  travel: FaPlane, flight: FaPlane, flights: FaPlane,
  recharge: FaMobileAlt, mobile: FaMobileAlt, phone: FaMobileAlt, telecom: FaMobileAlt,
  fashion: FaTshirt, clothing: FaTshirt, apparel: FaTshirt, lingerie: FaTshirt,
  food: FaUtensils, restaurant: FaUtensils, dining: FaUtensils, grocery: FaUtensils, groceries: FaUtensils,
  electronics: FaDesktop, tech: FaDesktop, gadgets: FaDesktop, computers: FaDesktop,
  shopping: FaShoppingCart, ecommerce: FaShoppingCart, 'online-shopping': FaShoppingCart,
  home: FaHome, furniture: FaHome, 'home-decor': FaHome,
  health: FaHeart, wellness: FaHeart, fitness: FaDumbbell, gym: FaDumbbell,
  beauty: FaHeart, cosmetics: FaHeart, skincare: FaHeart,
  automotive: FaCar, cars: FaCar, cab: FaCar, ride: FaCar,
  gaming: FaGamepad, games: FaGamepad,
  kids: FaBaby, baby: FaBaby, toys: FaBaby,
  books: FaBookOpen, education: FaBookOpen, courses: FaBookOpen, learning: FaBookOpen,
  pharmacy: FaPills, medicine: FaPills, medical: FaPills, medicines: FaCapsules,
  gifts: FaGift, gifting: FaGift, flowers: FaGift,
  business: FaBriefcase, office: FaBriefcase, hosting: FaBriefcase, software: FaBriefcase,
  entertainment: FaMusic, music: FaMusic, movies: FaMusic, streaming: FaMusic, ott: FaTv,
  photography: FaCamera,
  tools: FaWrench, hardware: FaWrench, services: FaWrench,
  bus: FaBus,
  bike: FaBicycle, 'bike-rentals': FaBicycle,
  eyewear: FaGlasses,
  footwear: FaShoePrints, shoes: FaShoePrints,
  jewellery: FaGem, jewelry: FaGem,
  'lab-tests': FaFlask, lab: FaFlask,
  pizza: FaPizzaSlice,
  hotel: FaHotel, hotels: FaHotel,
  kitchen: FaBlender, 'kitchen-appliances': FaBlender,
  sports: FaRunning,
  meat: FaDrumstickBite, dairy: FaDrumstickBite,
  protein: FaDumbbell, supplements: FaDumbbell,
  utility: FaWrench, 'bill-payments': FaWrench,
};

function getIcon(slug: string, name: string): IconType {
  const s = slug?.toLowerCase() || '';
  const n = name?.toLowerCase() || '';
  return (
    ICON_MAP[s] ||
    ICON_MAP[n] ||
    Object.entries(ICON_MAP).find(([k]) => s.includes(k) || n.includes(k))?.[1] ||
    FiTag
  );
}

export default function TopCoupons() {
  const { siteConfig, darkPalette, labels } = useDynamicTheme();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const primary = siteConfig?.theme?.primaryColor || '#7c3aed';
  const textColor = isDark ? darkPalette.text : siteConfig?.theme?.textColor || '#111827';
  const mutedText = isDark ? `${darkPalette.text}99` : `${textColor}80`;
  const cardBg = isDark ? darkPalette.cardBg : '#ffffff';
  const borderClr = isDark ? 'rgba(255,255,255,0.25)' : `${textColor}12`;
  const sectionBg = isDark ? darkPalette.bg : '#f9fafb';

  const [tabs, setTabs] = useState<{ label: string; slug: string; Icon: IconType }[]>([]);
  const [allCoupons, setAllCoupons] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('');
  const [fade, setFade] = useState(true);
  const [page, setPage] = useState(0);
  const [catPage, setCatPage] = useState(0);
  const [catsPerPage, setCatsPerPage] = useState(8);

  // Responsive categories per page
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setCatsPerPage(w < 640 ? 4 : w < 1024 ? 6 : 8);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    getCategories()
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const cats = (Array.isArray(data) ? data : []).map((c: any) => ({
          label: c.name,
          slug: c.slug || c.name.toLowerCase().replace(/\s+/g, '-'),
          Icon: getIcon(c.slug || '', c.name || ''),
        }));
        // Don't preselect a category — default view shows all, with coupons prioritized first
        if (cats.length > 0) setTabs(cats);
      })
      .catch(() => {});
    getCoupons({ limit: 50, sort: 'clickCount' })
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const serverUrl = getServerUrl();
        const mapped = (Array.isArray(data) ? data : []).map((c: any) => {
          const storeLogo = c.store?.logo
            ? c.store.logo.startsWith('http')
              ? c.store.logo
              : `${serverUrl}${c.store.logo}`
            : '';
          return {
            badge: c.discount || c.labelType || 'DEAL',
            title: c.title || '',
            store: c.store?.storeName || '',
            storeSlug: c.store?.slug || '',
            logo: c.customLogo || storeLogo,
            category: c.category?.name || c.category || '',
            tags: c.tags || [],
            link: c.affiliateUrl || c.store?.websiteUrl || '',
            hasCode: !!c.code,
          };
        });
        setAllCoupons(mapped);
      })
      .catch(() => {});
  }, []);

  const tabMatches = (c: any, label: string, slug: string) => {
    const cat = (c.category || '').toLowerCase().trim();
    const tabLabel = (label || '').toLowerCase().trim();
    const s = (slug || '').toLowerCase().trim();
    if (cat === tabLabel) return true;
    const catSlug = cat.replace(/[&\/]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '');
    if (catSlug === s) return true;
    if ((c.tags || []).some((t: string) => t.toLowerCase().trim() === tabLabel || t.toLowerCase().trim() === s)) return true;
    return false;
  };

  // Only show categories that actually have coupons/offers; categories with real
  // coupon codes come first (high priority), so the section always lands on coupons.
  const displayTabs = useMemo(() => {
    return tabs
      .map((t) => {
        const items = allCoupons.filter((c) => tabMatches(c, t.label, t.slug));
        return { ...t, total: items.length, codeCount: items.filter((c) => c.hasCode).length };
      })
      .filter((t) => t.total > 0)
      .sort((a, b) => (b.codeCount - a.codeCount) || (b.total - a.total));
  }, [tabs, allCoupons]);

  // Auto-select the top (highest-priority) category once data is ready
  useEffect(() => {
    if (!activeTab && displayTabs.length > 0) setActiveTab(displayTabs[0].label);
  }, [displayTabs, activeTab]);

  const activeSlug = tabs.find((t) => t.label === activeTab)?.slug || '';
  const filtered = !activeSlug
    ? allCoupons
    : allCoupons.filter((c) => {
        const cat = (c.category || '').toLowerCase().trim();
        const tabLabel = activeTab.toLowerCase().trim();
        const slug = activeSlug.toLowerCase().trim();
        
        // Match by exact category name
        if (cat === tabLabel) return true;
        
        // Match by category slug (convert category name to slug format for comparison)
        const catSlug = cat.replace(/[&\/]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '');
        if (catSlug === slug) return true;
        
        // Match by exact tag name
        if (c.tags.some((t: string) => t.toLowerCase().trim() === tabLabel || t.toLowerCase().trim() === slug)) return true;
        
        return false;
      });
  // Coupons (with a code) get top priority; popularity order preserved within each group
  const showAll = [...filtered].sort((a, b) => Number(b.hasCode) - Number(a.hasCode));

  const perPage = 6;
  const totalPages = Math.max(1, Math.ceil(showAll.length / perPage));
  const visible = showAll.slice(page * perPage, page * perPage + perPage);

  const switchTab = (label: string) => {
    setFade(false);
    setTimeout(() => {
      setActiveTab(label);
      setPage(0);
      setFade(true);
    }, 200);
  };

  if (allCoupons.length === 0) return null;

  return (
    <section className="py-8" style={{ backgroundColor: sectionBg }}>
      <div className="max-w-7xl mx-auto px-4">
        {(() => {
          const totalCatPages = Math.max(1, Math.ceil(displayTabs.length / catsPerPage));
          const visibleTabs = displayTabs.slice(catPage * catsPerPage, catPage * catsPerPage + catsPerPage);
          return (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold" style={{ color: textColor }}>
                  {labels?.homepage?.topCouponsTitle || "Today's Top Coupons & Offers"}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCatPage((p) => Math.max(0, p - 1))}
                    disabled={catPage === 0}
                    className="w-8 h-8 rounded-full flex items-center justify-center border transition-colors disabled:opacity-30"
                    style={{ borderColor: borderClr, color: mutedText, backgroundColor: cardBg }}
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex gap-1.5">
                    {Array.from({ length: totalCatPages }).map((_, i) => (
                      <span
                        key={i}
                        className="h-2 rounded-full transition-all cursor-pointer"
                        onClick={() => setCatPage(i)}
                        style={{
                          width: i === catPage ? 20 : 8,
                          backgroundColor:
                            i === catPage ? primary : isDark ? `${darkPalette.text}30` : '#d1d5db',
                        }}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => setCatPage((p) => Math.min(totalCatPages - 1, p + 1))}
                    disabled={catPage === totalCatPages - 1}
                    className="w-8 h-8 rounded-full flex items-center justify-center border transition-colors disabled:opacity-30"
                    style={{ borderColor: borderClr, color: mutedText, backgroundColor: cardBg }}
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {visibleTabs.length > 0 && (
                <div className="flex flex-wrap gap-2.5 mb-6">
                  {visibleTabs.map(({ label, Icon }) => (
                    <button
                      key={label}
                      onClick={() => switchTab(label)}
                      className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                      style={{
                        backgroundColor: activeTab === label ? primary : cardBg,
                        color: activeTab === label ? '#ffffff' : textColor,
                        borderColor:
                          activeTab === label ? primary : isDark ? `${darkPalette.text}30` : '#d1d5db',
                      }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 transition-opacity duration-300"
          style={{ opacity: fade ? 1 : 0 }}
        >
          {visible.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-lg font-semibold" style={{ color: textColor }}>No coupons in this category yet</p>
              <p className="text-sm mt-1" style={{ color: mutedText }}>Check back soon or browse other categories</p>
            </div>
          ) : visible.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                // Navigate to category page if a category tab is active, otherwise store
                if (activeSlug) {
                  window.location.href = `/coupons/${activeSlug}`;
                } else if (item.storeSlug) {
                  window.location.href = `/coupons/${item.storeSlug}`;
                }
              }}
              className="rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-2 flex flex-col cursor-pointer"
              style={{
                backgroundColor: cardBg,
                border: isDark ? '1px solid transparent' : `1px solid ${borderClr}`,
                boxShadow: isDark ? 'none' : 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
                minHeight: 180,
              }}
              onMouseEnter={(e) => { if (isDark) { e.currentTarget.style.border = `2px solid ${primary}`; e.currentTarget.style.boxShadow = `0 0 12px ${primary}40`; } }}
              onMouseLeave={(e) => { if (isDark) { e.currentTarget.style.border = '1px solid transparent'; e.currentTarget.style.boxShadow = 'none'; } }}
            >
              <div className="flex p-4 sm:p-5 gap-3 sm:gap-4 flex-1 items-center">
                <div className="flex items-center justify-center px-4 py-5 sm:px-6 sm:py-7 min-w-[80px] sm:min-w-[110px] flex-shrink-0">
                  <div className="flex w-fit flex-col self-center text-center font-black uppercase tracking-tight mx-auto text-2xl sm:text-4xl md:text-5xl" style={{ color: primary }}>
                    {(() => {
                      const badge = item.badge || '';
                      // Extract number and suffix
                      const match = badge.match(/^(\d+)(%?\s*)(.*)?$/i);
                      if (match) {
                        const num = match[1];
                        const pct = match[2]?.trim() || '%';
                        const suffix = match[3]?.trim() || 'Off';
                        return (
                          <>
                            <p className="leading-none">{num}{pct.includes('%') ? '%' : '%'}</p>
                            <p className="leading-none">{suffix}</p>
                          </>
                        );
                      }
                      // Non-numeric badges like "DEAL", "FREE"
                      return <p className="leading-none">{badge}</p>;
                    })()}
                  </div>
                </div>
                <div
                  className="border-l border-dashed self-stretch"
                  style={{ borderColor: borderClr }}
                />
                <div
                  className="text-base sm:text-lg font-semibold leading-snug flex items-center"
                  style={{ color: textColor }}
                >
                  {item.title}
                </div>
              </div>
              <div
                className="flex justify-between items-center border-t px-3 sm:px-4 py-2.5 sm:py-3"
                style={{ borderColor: borderClr, backgroundColor: sectionBg }}
              >
                <div className="flex items-center gap-2">
                  {item.logo && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={getImageUrl(item.logo, { width: 160 })}
                      alt={item.store}
                      loading="lazy"
                      decoding="async"
                      className="h-10 sm:h-12 md:h-14 object-fill"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}

                </div>
                {item.store && (
                  <div
                    className="flex items-center gap-1 text-sm cursor-pointer hover:underline"
                    style={{ color: primary }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.storeSlug)
                        window.location.href = `/coupons/${item.storeSlug}`;
                    }}
                  >
                    View All {item.store} Coupons
                    <FiExternalLink size={14} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
